/**
 * L'accueil : on est combien, qui joue, et à qui on a affaire.
 *
 * L'ordre des questions est celui d'une vraie table, et c'est celui qu'Olivier a
 * demandé : **combien on est**, puis **qui joue pour de vrai**. Le nombre
 * d'adversaires virtuels s'en déduit — personne n'a à le calculer.
 *
 * Le niveau porte le nom de l'adversaire (« Le taulier ») et son taux de défaite
 * mesuré, parce qu'une échelle de 1 à 8 ne dit rien : ces chiffres viennent de
 * 150 000 parties par point (`sim/CALIBRATION.md`) et c'est la seule façon honnête
 * d'annoncer une difficulté.
 */

import { PROFILS, type Niveau } from "@zinc421/shared";

import { useLangue } from "../i18n/contexte.js";
import { LANGUES } from "../i18n/index.js";
import { TABLE_MAX, TABLE_MIN } from "./session.js";

/**
 * Taux de défaite mesurés à quatre joueurs face à des tauliers, sièges permutés
 * (`sim/CALIBRATION.md`, avec le sec doublé). Plus le taux est bas, plus il est fort.
 */
const DEFAITES: Readonly<Record<number, string>> = {
  1: "60,0 %", 2: "53,5 %", 3: "51,3 %", 4: "39,2 %",
  5: "36,8 %", 6: "34,4 %", 7: "28,2 %", 8: "25,2 %",
};

interface Props {
  readonly total: number;
  readonly humains: readonly string[];
  readonly niveau: Niveau;
  readonly onTotal: (n: number) => void;
  readonly onHumains: (noms: readonly string[]) => void;
  readonly onNiveau: (n: Niveau) => void;
  readonly onCommencer: () => void;
  readonly onCampagne: () => void;
  /** Ce qu'on peut reprendre, en une ligne, ou `null` s'il n'y a rien en plan. */
  readonly reprise: string | null;
  readonly onReprendre: () => void;
  readonly onEnLigne: () => void;
  readonly onBacASable: () => void;
  /** Le son et l'haptique. Réglable ici parce que c'est là qu'on cherche un réglage. */
  readonly son: boolean;
  readonly onSon: (actif: boolean) => void;
  readonly enPreparation: boolean;
}

export function Accueil({
  total, humains, niveau, onTotal, onHumains, onNiveau,
  onCommencer, onCampagne, reprise, onReprendre, onEnLigne, onBacASable,
  son, onSon, enPreparation,
}: Props) {
  const { langue, textes, changerLangue } = useLangue();
  const virtuels = total - humains.length;

  const changerNom = (index: number, valeur: string) => {
    onHumains(humains.map((nom, i) => (i === index ? valeur : nom)));
  };

  return (
    <div className="accueil">
      <h1>{textes.appNom}</h1>
      <p className="accroche">{textes.accueilAccroche}</p>

      <fieldset className="reglage">
        <legend>{textes.combienATable}</legend>
        <div className="choix">
          {Array.from({ length: TABLE_MAX - TABLE_MIN + 1 }, (_, i) => TABLE_MIN + i)
            .map((n) => (
              <button
                key={n}
                type="button"
                className={`puce${n === total ? " puce--choisie" : ""}`}
                aria-pressed={n === total}
                onClick={() => onTotal(n)}
              >
                {n}
              </button>
            ))}
        </div>
      </fieldset>

      <fieldset className="reglage">
        <legend>
          {textes.quiJoue}{" "}
          {virtuels > 0 ? textes.adversairesVirtuels(virtuels) : textes.tableToutHumaine}
        </legend>
        <ul className="humains">
          {humains.map((nom, i) => (
            <li key={i}>
              <input
                className="nom"
                type="text"
                value={nom}
                maxLength={16}
                aria-label={textes.nomDuJoueur(i + 1)}
                placeholder={textes.joueurNumero(i + 1)}
                onChange={(e) => changerNom(i, e.target.value)}
              />
              {humains.length > 1 && (
                <button
                  type="button"
                  className="retirer"
                  aria-label={textes.retirerJoueur(nom || textes.joueurNumero(i + 1))}
                  onClick={() => onHumains(humains.filter((_, j) => j !== i))}
                >
                  −
                </button>
              )}
            </li>
          ))}
        </ul>
        {humains.length < total && (
          <button
            type="button"
            className="bouton bouton--discret"
            onClick={() => onHumains([...humains, ""])}
          >
            {textes.ajouterUnJoueur}
          </button>
        )}
      </fieldset>

      {virtuels > 0 && (
        <fieldset className="reglage">
          <legend>{textes.enFace}</legend>
          <ul className="niveaux">
            {PROFILS.map((profil) => (
              <li key={profil.niveau}>
                <button
                  type="button"
                  className={`niveau${profil.niveau === niveau ? " niveau--choisi" : ""}`}
                  aria-pressed={profil.niveau === niveau}
                  onClick={() => onNiveau(profil.niveau)}
                >
                  <span className="niveau-nom">{textes.adversaireNom(profil.cle)}</span>
                  <span className="niveau-taux">
                    {textes.perdTaux(DEFAITES[profil.niveau] ?? "")}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </fieldset>
      )}

      {/* La reprise passe AVANT « Jouer » : quelqu'un qui rouvre l'application au
          milieu d'une partie vient pour elle, pas pour en commencer une autre. */}
      {reprise && (
        <button
          className="bouton bouton--large bouton--reprise"
          onClick={onReprendre}
          disabled={enPreparation}
        >
          <span>{textes.reprendreLaPartie}</span>
          <span className="reprise-ou">{reprise}</span>
        </button>
      )}

      <button className="bouton bouton--large" onClick={onCommencer} disabled={enPreparation}>
        {enPreparation ? textes.onPrepareLaTable : textes.jouer}
      </button>
      <button className="bouton bouton--large" onClick={onCampagne}>
        {textes.laTournee}
      </button>
      <button className="bouton bouton--large" onClick={onEnLigne}>
        {textes.jouerEnLigne}
      </button>
      <button className="bouton bouton--discret" onClick={onBacASable}>
        {textes.justeLancerLesDes}
      </button>
      <button
        className="bouton bouton--discret"
        aria-pressed={son}
        onClick={() => onSon(!son)}
      >
        {son ? textes.sonActif : textes.sonCoupe}
      </button>

      {/* La langue, en dernier : on la change une fois, et jamais deux. Les deux
          noms sont écrits DANS leur propre langue — personne ne cherche « Anglais »
          dans une interface qu'il ne comprend pas. */}
      <fieldset className="reglage">
        <legend>{textes.choisirLaLangue}</legend>
        <div className="choix">
          {LANGUES.map((l) => (
            <button
              key={l.code}
              type="button"
              lang={l.code}
              className={`puce${l.code === langue ? " puce--choisie" : ""}`}
              aria-pressed={l.code === langue}
              onClick={() => changerLangue(l.code)}
            >
              {l.nom}
            </button>
          ))}
        </div>
      </fieldset>
    </div>
  );
}
